import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { PageContainer } from '@toolpad/core/PageContainer';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SharedDropzone from '../components/common/SharedDropzone';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { apiService } from '../services/api';
import { logTenderActivity } from '../utils/activityLogger';

interface Props {
  isWizardMode?: boolean;
  onSuccess?: (tenderId?: string) => void;
  onBrowsePublic?: () => void;
  onManualEntry?: () => void;
}

const AddTenderPage = ({ isWizardMode, onSuccess, onBrowsePublic, onManualEntry }: Props = {}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { idToken } = useAuth();
  const { showSnackbar } = useSnackbar();

  // Upload State
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isAddingRFP, setIsAddingRFP] = useState(false);
  const [addRFPError, setAddRFPError] = useState('');

  // INTERIM WHITE-LABELING: State for Grant toggle
  const [isGrant, setIsGrant] = useState(location.state?.isGrant || false);

  // Handlers
  const handleRFPFilesSelectedForUpload = (files: File[]) => {
    if (files && files.length > 0) {
      const validFiles = files.filter((f) => f instanceof File);
      if (validFiles.length > 0) {
        setStagedFiles((prev) => {
          const newFiles = validFiles.filter(
            (vf) => !prev.find((pf) => pf.name === vf.name && pf.size === vf.size)
          );
          return [...prev, ...newFiles];
        });
        setUploadDialogOpen(true);
        setAddRFPError('');
      }
    }
  };

  const handleAddMoreFiles = (files: File[]) => {
    const validFiles = files.filter((f) => f instanceof File);
    if (validFiles.length > 0) {
      setStagedFiles((prev) => {
        const newFiles = validFiles.filter(
          (vf) => !prev.find((pf) => pf.name === vf.name && pf.size === vf.size)
        );
        if (newFiles.length === 0) {
          showSnackbar('Selected file(s) already staged.', 'info');
        }
        return [...prev, ...newFiles];
      });
    }
  };

  const handleRemoveStagedFile = (fileName: string) => {
    setStagedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const handleCancelUpload = () => {
    setStagedFiles([]);
    setUploadDialogOpen(false);
    setIsAddingRFP(false);
    setAddRFPError('');
  };

  const handleConfirmUpload = async () => {
    if (stagedFiles.length === 0) {
      showSnackbar('No files selected for upload.', 'warning');
      return;
    }
    if (!idToken) {
      showSnackbar('Authentication error. Cannot upload files.', 'error');
      return;
    }
    setIsAddingRFP(true);
    setAddRFPError('');
    try {
      // INTERIM WHITE-LABELING: Pass isGrant in metadata
      const response = await apiService.createTender(stagedFiles, 'uploaded', { isGrant });
      showSnackbar(`Successfully started processing ${stagedFiles.length} file(s).`, 'success');

      logTenderActivity.created({
        title: stagedFiles[0]?.name || 'Uploaded Tender',
        type: 'upload',
        source: 'creation_hub_dropzone',
        fileCount: stagedFiles.length,
      });

      setStagedFiles([]);
      setUploadDialogOpen(false);

      if (isWizardMode && onSuccess) {
        onSuccess(response.tenderId);
      } else {
        navigate('/dashboard'); // Redirect to dashboard to see progress
      }
    } catch (uploadError: unknown) {
      const error = uploadError as {
        response?: { data?: { message?: string } | string };
        message?: string;
      };
      const errorData = error.response?.data;
      const errorMsg =
        (typeof errorData === 'object' && errorData?.message) ||
        (typeof errorData === 'string' ? errorData : null) ||
        error.message ||
        'Upload failed.';
      setAddRFPError(errorMsg);
      showSnackbar(`Upload failed: ${errorMsg}`, 'error');
    } finally {
      setIsAddingRFP(false);
    }
  };

  const handleDialogClose = (
    _event: React.SyntheticEvent,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (isAddingRFP) {
        showSnackbar('Please wait for the upload to complete.', 'info');
        return;
      } else {
        handleCancelUpload();
      }
    } else {
      handleCancelUpload();
    }
  };

  const content = (
    <>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, md: 3 } }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, textAlign: 'center', color: theme.palette.text.secondary }}
        >
          Choose how you want to start your next pursuit.
        </Typography>

        {/* INTERIM WHITE-LABELING: Grant Toggle */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 4,
            bgcolor: theme.palette.background.paper,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              mr: 1,
              color: isGrant ? 'text.disabled' : theme.palette.primary.main,
              fontSize: '1.2rem',
            }}
          >
            Tender
          </Typography>
          <Switch
            checked={isGrant}
            onChange={(e) => setIsGrant(e.target.checked)}
            color="success"
            sx={{ transform: 'scale(1.5)', mx: 2 }}
          />
          <Typography
            sx={{
              fontWeight: 'bold',
              ml: 1,
              color: isGrant ? theme.palette.success.main : 'text.disabled',
              fontSize: '1.2rem',
            }}
          >
            Grant
          </Typography>
        </Box>
        {/* END INTERIM WHITE-LABELING */}

        <Grid container spacing={4} alignItems="stretch" justifyContent="center">
          {/* Option 1: Quick Upload */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: isGrant
                  ? `2px solid ${theme.palette.success.main}`
                  : `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* INTERIM WHITE-LABELING: Visual indicator */}
              {isGrant && (
                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
                  <Chip label="Grant Mode" color="success" sx={{ fontWeight: 'bold' }} />
                </Box>
              )}
              <Box
                sx={{
                  p: 2,
                  bgcolor: isGrant
                    ? `${theme.palette.success.main}15`
                    : theme.palette.mode === 'dark'
                      ? theme.palette.grey[800]
                      : theme.palette.grey[100],
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <CloudUploadIcon
                  sx={{
                    fontSize: 40,
                    color: isGrant ? theme.palette.success.main : theme.palette.primary.main,
                  }}
                />
                <Typography variant="h6" sx={{ mt: 1, textAlign: 'center' }}>
                  Quick Upload
                </Typography>
              </Box>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body1" sx={{ mb: 2, textAlign: 'center', fontSize: '1.1rem' }}>
                  Have the {isGrant ? 'Grant' : 'RFT'} documents ready? Drop them here to instantly
                  create a {isGrant ? 'grant' : 'tender'}.
                </Typography>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <SharedDropzone
                    onFilesSelected={handleRFPFilesSelectedForUpload}
                    dropzoneText={`Drop ${isGrant ? 'Grant' : 'RFT'} files here`}
                    maxFiles={2000}
                    maxSize={314572800}
                    type="tender_docs"
                    onFileUploadError={(msg) => showSnackbar(msg, 'warning')}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Option 2: Manual Entry */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                height: '100%',
                border: isGrant
                  ? `2px solid ${theme.palette.success.main}`
                  : `1px solid ${theme.palette.divider}`,
                position: 'relative',
              }}
            >
              {/* INTERIM WHITE-LABELING: Visual indicator */}
              {isGrant && (
                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
                  <Chip label="Grant Mode" color="success" sx={{ fontWeight: 'bold' }} />
                </Box>
              )}
              {/* INTERIM WHITE-LABELING: Pass isGrant via navigation state */}
              <CardActionArea
                onClick={() => {
                  if (isWizardMode && onManualEntry) {
                    onManualEntry();
                  } else {
                    navigate('/manual-tender', { state: { isGrant } });
                  }
                }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: isGrant
                      ? `${theme.palette.success.main}15`
                      : theme.palette.mode === 'dark'
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <DescriptionIcon
                    sx={{
                      fontSize: 40,
                      color: isGrant
                        ? theme.palette.success.main
                        : theme.palette.bidlogiq?.brick || 'primary.main',
                    }}
                  />
                  <Typography variant="h6" sx={{ mt: 1, textAlign: 'center' }}>
                    Manual Entry
                  </Typography>
                </Box>
                <CardContent>
                  <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '1.1rem' }}>
                    Need to enter details manually? Use our form to input requirements, objectives,
                    and scope for ad-hoc requests.
                  </Typography>
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        bgcolor: isGrant
                          ? theme.palette.success.main
                          : theme.palette.bidlogiq?.brick,
                        color: 'white',
                        fontSize: '1.1rem',
                        padding: '10px 24px',
                        borderRadius: 1,
                        fontWeight: 500,
                        boxShadow: 2,
                        textTransform: 'uppercase',
                        '&:hover': {
                          bgcolor: isGrant
                            ? theme.palette.success.dark
                            : theme.palette.bidlogiq?.rust,
                        },
                      }}
                    >
                      Go to Form
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          {/* Option 3: Browse Store - INTERIM WHITE-LABELING: Adapted for grants */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={{
                height: '100%',
                border: `1px solid ${theme.palette.divider}`,
                opacity: isGrant ? 0.7 : 1,
              }}
            >
              <CardActionArea
                onClick={() => {
                  if (!isGrant) {
                    if (isWizardMode && onBrowsePublic) {
                      onBrowsePublic();
                    } else {
                      navigate('/public-tenders');
                    }
                  }
                }}
                disabled={isGrant}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    width: '100%',
                  }}
                >
                  <StorefrontIcon
                    sx={{
                      fontSize: 40,
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.bidlogiq?.camel
                          : theme.palette.bidlogiq?.rust || 'error.main',
                    }}
                  />
                  <Typography variant="h6" sx={{ mt: 1, textAlign: 'center' }}>
                    {isGrant ? 'Browse Public Grants' : 'Browse Public Tenders'}
                  </Typography>
                </Box>
                <CardContent>
                  <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '1.1rem' }}>
                    {isGrant
                      ? 'Looking for opportunities? Browse hundreds of public grants and add them to your dashboard.'
                      : 'Looking for opportunities? Browse hundreds of public tenders and add them to your dashboard.'}
                  </Typography>
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    {isGrant ? (
                      <Chip label="Coming soon!" color="info" sx={{ fontWeight: 600 }} />
                    ) : (
                      <Box
                        sx={{
                          display: 'inline-block',
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.bidlogiq?.camel
                              : theme.palette.bidlogiq?.rust,
                          color: 'white',
                          fontSize: '1.1rem',
                          padding: '10px 24px',
                          borderRadius: 1,
                          fontWeight: 500,
                          boxShadow: 2,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? theme.palette.bidlogiq?.camel
                                : theme.palette.bidlogiq?.rust,
                            opacity: 0.9,
                          },
                        }}
                      >
                        Explore Public Tenders
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Upload Dialog (Reused Logic) */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          style: { backdropFilter: 'blur(5px)', backgroundColor: 'rgba(0,0,0,0.5)' },
        }}
      >
        <DialogTitle
          sx={{ bgcolor: theme.palette.bidlogiq?.charcoal || 'grey.900', color: 'white' }}
        >
          {isAddingRFP ? 'Upload in Progress' : `Review and Create ${isGrant ? 'Grant' : 'Tender'}`}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <List dense>
            {stagedFiles.map((file) => (
              <ListItem
                key={`${file.name}-${file.size}`}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveStagedFile(file.name)}
                    disabled={isAddingRFP}
                  >
                    <ClearIcon />
                  </IconButton>
                }
                sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
              >
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                />
              </ListItem>
            ))}
          </List>
          {addRFPError && (
            <Typography color="error" sx={{ mt: 1, textAlign: 'center' }}>
              {addRFPError}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <SharedDropzone
              onFilesSelected={handleAddMoreFiles}
              isLoading={isAddingRFP}
              dropzoneText="Add more files"
              type="tender_docs"
              onFileUploadError={(msg) => showSnackbar(msg, 'warning')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCancelUpload} disabled={isAddingRFP}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            disabled={isAddingRFP || stagedFiles.length === 0}
            startIcon={
              isAddingRFP ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />
            }
            sx={{
              bgcolor: isGrant
                ? theme.palette.success.main
                : theme.palette.bidlogiq?.brick || 'primary.main',
              color: 'white',
            }}
          >
            {isAddingRFP ? 'Creating...' : `Create ${isGrant ? 'Grant' : 'Tender'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return isWizardMode ? (
    content
  ) : (
    <PageContainer data-testid="add-tender-page" maxWidth={false}>
      {content}
    </PageContainer>
  );
};

export default AddTenderPage;
